import React from "react";

const providers = [
  {
    name: "Counseling & Wellness Services of Archbold",
    focus: "Mental health counseling in Archbold, Ohio",
    services: "Individual counseling · EMDR therapy · Couples & family counseling",
    description:
      "Licensed counseling for adults, children, couples, and families, including support for anxiety, depression, trauma, addiction, relationship concerns, and life changes.",
    href: "https://www.counselingarchbold.com/",
    image: "/images/counseling-logo.png",
    imageClass: "counseling-logo",
    width: 536,
    height: 352,
  },
  {
    name: "Grow Your Mind Therapy",
    focus: "Pediatric therapy in northwest Ohio",
    services: "Speech & language therapy · Occupational therapy · Psychological testing",
    description:
      "Individualized care for communication, fine motor, sensory, self-care, learning, behavioral, and emotional needs—with support for children, families, and schools.",
    href: "https://www.growyourmindtherapy.com/",
    image: "/images/grow-logo.png",
    imageClass: "grow-logo",
    width: 813,
    height: 813,
  },
  {
    name: "Selah Wellness Studio",
    focus: "Natural wellness & restorative care",
    services: "Naturopathic care · Fascia & body trauma release · Body sculpting",
    description:
      "Root-cause wellness support through homeopathic remedies, fascia and body trauma release, reflexology, skin care, and nutrition guidance—plus red light sauna and nature-inspired self-care products.",
    href: "https://selahwellnessstudio.net/",
    image: "/images/selah-logo.jpg",
    imageClass: "selah-logo",
    stageClass: "selah-stage",
    width: 1280,
    height: 853,
  },
];

const communities = [
  "Archbold, Ohio",
  "Wauseon",
  "Bryan",
  "Defiance",
  "Napoleon",
  "Montpelier",
  "West Unity",
  "Stryker",
  "Pettisville",
  "Fayette",
  "Delta",
  "Swanton",
  "Liberty Center",
];

const faqItems = [
  {
    question: "Where can I find mental health counseling or a therapist in Archbold, Ohio?",
    answer:
      "Counseling & Wellness Services of Archbold provides mental health and addiction counseling for adults, children, couples, and families at the Archbold Wellness location in downtown Archbold, Ohio. Contact the practice directly to ask about therapists, scheduling, and current availability.",
  },
  {
    question: "Is EMDR, marriage counseling, or family counseling available in Archbold, Ohio?",
    answer:
      "Yes. Counseling & Wellness Services of Archbold offers EMDR therapy along with individual, couples, marital, and family counseling. The provider can help determine which approach fits your needs.",
  },
  {
    question: "Where can families find pediatric speech and occupational therapy near Archbold, Ohio?",
    answer:
      "Grow Your Mind Therapy provides pediatric speech and language therapy, occupational therapy, psychological testing, and school support for children and families in northwest Ohio.",
  },
  {
    question: "What holistic and restorative wellness services are available in Archbold, Ohio?",
    answer:
      "Selah Wellness Studio offers naturopathic wellness care, homeopathic remedies, fascia and body trauma release, reflexology, non-invasive body sculpting, skin care, nutrition guidance, red light sauna, and nature-inspired self-care products.",
  },
  {
    question: "What communities are near the Archbold Wellness location?",
    answer:
      "The downtown location in Archbold, Ohio, is convenient to communities across Fulton, Williams, Defiance, and Henry counties, including Wauseon, Bryan, Defiance, Napoleon, Montpelier, West Unity, Stryker, Pettisville, Fayette, Delta, Swanton, and Liberty Center. Availability varies by provider.",
  },
];

const directionsUrl =
  "https://www.google.com/maps/dir/?api=1&destination=202%20N.%20Defiance%20St.%2C%20Archbold%2C%20OH%2043502";

function LeafMark({ small = false }) {
  return (
    <span className={`leaf-mark${small ? " leaf-mark-small" : ""}`} aria-hidden="true">
      <span className="leaf leaf-one" />
      <span className="leaf leaf-two" />
      <span className="leaf-stem" />
    </span>
  );
}

function Wordmark({ footer = false }) {
  return (
    <a
      className={`wordmark${footer ? " footer-wordmark" : ""}`}
      href="#top"
      aria-label={footer ? undefined : "Archbold Wellness home"}
    >
      <LeafMark small />
      <span>
        Archbold <em>Wellness</em>
      </span>
    </a>
  );
}

function NavLinks({ mobile = false }) {
  return (
    <>
      <a href="#providers">Our providers</a>
      <a href="#space">Our space</a>
      <a href="#areas">Service area</a>
      <a href="#location">Location</a>
      <a className={mobile ? undefined : "nav-cta"} href="#leasing">
        Now leasing
      </a>
    </>
  );
}

function Header() {
  return (
    <header className="site-header">
      <Wordmark />
      <nav className="desktop-nav" aria-label="Primary navigation">
        <NavLinks />
      </nav>
      <details className="mobile-menu">
        <summary>Menu</summary>
        <nav aria-label="Mobile navigation">
          <NavLinks mobile />
        </nav>
      </details>
    </header>
  );
}

function BotanicalRule() {
  return (
    <div className="botanical-rule" aria-hidden="true">
      <span />
      <LeafMark small />
      <span />
    </div>
  );
}

function Hero() {
  return (
    <>
      <section
        className="hero"
        aria-labelledby="hero-title"
      >
        <div className="hero-copy">
          <p className="eyebrow">Archbold Wellness</p>
          <h1 id="hero-title">
            <span className="hero-title-line">Independent care.</span>
            <span className="hero-title-line">Shared community.</span>
            <span className="hero-services">
              Counseling <span aria-hidden="true">•</span> Pediatric therapy <span aria-hidden="true">•</span>{" "}
              Wellness in Archbold, Ohio
            </span>
          </h1>
          <p className="hero-address">
            202 N. Defiance St. <span aria-hidden="true">•</span> Archbold, Ohio
          </p>
          <BotanicalRule />
          <p className="hero-intro">
            Looking for counseling, pediatric speech and occupational therapy, or restorative wellness in
            Archbold, Ohio? Meet three independent practices at 202 N. Defiance Street.
          </p>
          <div className="button-row">
            <a className="button button-primary" href="#providers">
              Meet our providers
            </a>
            <a className="button button-secondary" href={directionsUrl} target="_blank" rel="noreferrer">
              Get directions <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <div className="hero-visual">
          <img
            src="/images/ai-building-arrival-lit.jpg"
            alt="AI-created scene of a visitor arriving at the 202 North Defiance Street wellness building"
            width="1280"
            height="853"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="hero-badge">
            <span>In the heart of Archbold, Ohio</span>
            <strong>202 N. Defiance St.</strong>
          </div>
          <div className="hero-ring ring-one" aria-hidden="true" />
          <div className="hero-ring ring-two" aria-hidden="true" />
        </div>
      </section>
      <section className="welcome-strip" aria-label="Archbold Wellness introduction">
        <p>A shared home for trusted local providers—each with their own expertise, approach, and path to care.</p>
      </section>
    </>
  );
}

function Providers() {
  return (
    <section className="providers-section" id="providers" aria-labelledby="providers-title">
      <div className="section-heading">
        <p className="eyebrow">Find the care that fits</p>
        <h2 id="providers-title">Meet our providers</h2>
        <p>
          Each practice operates independently. Choose a provider below to learn about services, availability,
          and how to get started.
        </p>
      </div>
      <div className="provider-grid">
        {providers.map((provider, index) => (
          <article className="provider-card" key={provider.name}>
            <div className="provider-number" aria-hidden="true">
              0{index + 1}
            </div>
            <div className={`logo-stage${provider.stageClass ? ` ${provider.stageClass}` : ""}`}>
              <img
                className={`provider-logo ${provider.imageClass}`}
                src={provider.image}
                alt={`${provider.name} logo`}
                width={provider.width}
                height={provider.height}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="provider-content">
              <p className="provider-focus">{provider.focus}</p>
              <h3>{provider.name}</h3>
              <p className="provider-services">{provider.services}</p>
              <p>{provider.description}</p>
              <a href={provider.href}>
                Visit {provider.name} <span aria-hidden="true">↗</span>
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Space() {
  return (
    <section className="space-section" id="space" aria-labelledby="space-title">
      <div className="space-gallery" aria-label="A look inside the shared wellness space">
        <figure className="gallery-main">
          <img
            src="/images/ai-welcoming-office.jpg"
            alt="AI-created view of a warm, private professional wellness office"
            width="1536"
            height="1024"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <figure className="gallery-small">
          <img
            src="/images/ai-pediatric-therapy.jpg"
            alt="AI-created pediatric therapy scene with a therapist and child"
            width="1536"
            height="1024"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <div className="gallery-note">
          <img
            src="/images/ai-wellness-studio.jpg"
            alt="AI-created restorative wellness studio"
            width="1536"
            height="1024"
            loading="lazy"
            decoding="async"
          />
          <div className="gallery-note-copy">
            <LeafMark />
            <span>Welcoming care for children, adults, couples, and families</span>
          </div>
        </div>
      </div>
      <div className="space-copy">
        <p className="eyebrow">One address. Many ways to feel supported.</p>
        <h2 id="space-title">A space designed for care and connection.</h2>
        <p>
          Archbold Wellness brings complementary providers together in a convenient downtown location. Visitors
          can connect with the practice that best fits their needs while enjoying a calm, private, and
          approachable setting.
        </p>
        <ul className="feature-list">
          <li>
            <span>01</span>
            <div>
              <strong>Independent practices</strong>
              <p>Contact and schedule directly with the provider you choose.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <strong>Central location in Archbold, Ohio</strong>
              <p>Easy to find at 202 N. Defiance Street in downtown Archbold, Ohio.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <strong>Whole-person perspective</strong>
              <p>Mental, developmental, and restorative wellness under one roof.</p>
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}

function Regional() {
  return (
    <section className="regional-section" id="areas" aria-labelledby="regional-title">
      <div className="regional-intro">
        <p className="eyebrow">Local care for northwest Ohio</p>
        <h2 id="regional-title">Counseling, pediatric therapy, and wellness in Archbold, Ohio.</h2>
        <p>
          Archbold Wellness makes it easier to find local counseling, pediatric therapy services, and holistic
          wellness support—all in downtown Archbold, Ohio. Each practice is independently operated, so you can
          connect directly with the provider that matches your needs.
        </p>
      </div>
      <div className="service-intent-grid" aria-label="Services available in Archbold, Ohio">
        <article>
          <span>01</span>
          <h3>Mental health counseling</h3>
          <p>
            Individual counseling, EMDR therapy, trauma and addiction support, and marriage, couples, and family
            counseling for children and adults.
          </p>
        </article>
        <article>
          <span>02</span>
          <h3>Pediatric therapy</h3>
          <p>
            Speech and language therapy, occupational therapy, psychological testing, and school support centered
            on each child’s growth and daily life.
          </p>
        </article>
        <article>
          <span>03</span>
          <h3>Restorative wellness</h3>
          <p>
            Body wellness, red light sauna, fascia and reflexology care, body contouring, skin care, and natural
            lifestyle support.
          </p>
        </article>
      </div>
      <div className="area-panel">
        <div>
          <p className="eyebrow">Within about 35 miles</p>
          <h3>Welcoming northwest Ohio communities.</h3>
          <p>
            Convenient to Fulton, Williams, Defiance, and Henry counties. Provider availability and individual
            service areas may vary.
          </p>
        </div>
        <ul aria-label="Nearby communities">
          {communities.map((community) => (
            <li key={community}>{community}</li>
          ))}
        </ul>
      </div>
      <div className="faq-block" aria-labelledby="faq-title">
        <div>
          <p className="eyebrow">Common questions</p>
          <h3 id="faq-title">Finding care near Archbold, Ohio</h3>
        </div>
        <div className="faq-list">
          {faqItems.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Leasing() {
  return (
    <section className="leasing-section" id="leasing" aria-labelledby="leasing-title">
      <div className="leasing-art" aria-hidden="true">
        <LeafMark />
      </div>
      <div>
        <p className="eyebrow">Space for your practice</p>
        <h2 id="leasing-title">Want to join our group?</h2>
        <p>
          We’re now leasing professional space to providers who share a thoughtful, community-centered approach to
          wellness.
        </p>
      </div>
      <a
        className="button button-light"
        href="mailto:leasing@archboldwellness.com?subject=Archbold%20Wellness%20leasing%20inquiry"
      >
        Ask about available space
      </a>
    </section>
  );
}

function Location() {
  return (
    <section className="location-section" id="location" aria-labelledby="location-title">
      <div className="location-details">
        <div>
          <p className="eyebrow">Visit us</p>
          <h2 id="location-title">In downtown Archbold, Ohio.</h2>
        </div>
        <address>
          <strong>Archbold Wellness</strong>
          <span>202 N. Defiance St.</span>
          <span>Archbold, Ohio 43502</span>
        </address>
        <p className="location-note">
          Explore the map right here, or open turn-by-turn directions when you’re ready to visit.
        </p>
        <a className="text-link" href={directionsUrl} target="_blank" rel="noreferrer">
          Get directions <span aria-hidden="true">↗</span>
        </a>
      </div>
      <div className="location-map">
        <iframe
          title="Map showing Archbold Wellness at 202 North Defiance Street in Archbold, Ohio"
          src="https://www.google.com/maps?q=202%20North%20Defiance%20Street%2C%20Archbold%2C%20OH%2043502&output=embed"
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-top">
        <Wordmark footer />
        <div className="footer-links">
          {providers.map((provider) => (
            <a href={provider.href} key={provider.name}>
              {provider.name}
            </a>
          ))}
          <a
            href="https://www.facebook.com/profile.php?id=61591936964472"
            target="_blank"
            rel="noreferrer"
          >
            Archbold Wellness on Facebook
          </a>
        </div>
      </div>
      <div className="crisis-note">
        <p>
          If this is an emergency, call 911 or go to your nearest emergency room. You may also call or text{" "}
          <a href="tel:988">988</a> for crisis support. Archbold Wellness is not a crisis service.
        </p>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Archbold Wellness</span>
        <span>202 N. Defiance St. · Archbold, Ohio</span>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <Header />
      <main id="top">
        <Hero />
        <Providers />
        <Space />
        <Regional />
        <Leasing />
        <Location />
      </main>
      <Footer />
    </>
  );
}
